import { useState, useCallback } from 'react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface Option {
  id: string;
  label: string;
}

interface QuickOptionsProps {
  options: Option[];
  multiSelect?: boolean;
  onSelect: (optionId: string) => void;
  onMultiSelect?: (optionIds: string[]) => void;
  disabled?: boolean;
}

/**
 * Quick selection buttons for multiple choice questions.
 * Supports single-select and multi-select modes.
 */
export function QuickOptions({
  options,
  multiSelect = false,
  onSelect,
  onMultiSelect,
  disabled = false,
}: QuickOptionsProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const handleToggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    if (selected.size > 0) {
      onMultiSelect?.(Array.from(selected));
    }
  }, [selected, onMultiSelect]);

  if (multiSelect) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => handleToggle(option.id)}
              disabled={disabled}
              className={cn(
                'rounded-full px-4 py-2 text-sm font-medium transition-all',
                'border border-border',
                'hover:border-primary/50 hover:bg-accent',
                'focus:outline-none focus:ring-2 focus:ring-primary/20',
                'disabled:cursor-not-allowed disabled:opacity-50',
                selected.has(option.id) &&
                  'border-primary bg-primary/10 text-primary'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        <Button
          onClick={handleSubmit}
          disabled={disabled || selected.size === 0}
          size="sm"
          className="self-start"
        >
          Confirm Selection
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onSelect(option.id)}
          disabled={disabled}
          className={cn(
            'rounded-full px-4 py-2 text-sm font-medium transition-all',
            'border border-border bg-card',
            'hover:border-primary hover:bg-accent',
            'focus:outline-none focus:ring-2 focus:ring-primary/20',
            'active:scale-95',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
